import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box/Box';
import ListItemText1 from 'Components/ComponentLibrary/Components/Uncategorized/ListItemText/ListItemText1';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import ShadedRoundBox from 'Components/ComponentLibrary/Atoms/Ornament/ShadedRoundBox';
import PrioritySwitcher from 'Components/ComponentLibrary/Atoms/Inputs/PrioritySwitcher';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  listItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  orderActions: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    flex: 0,
  },
});

const SelectFieldPrioritization = ({
  className,
  classes,
  onChange,
  options,
  value,
}) => {
  const [prioritizationItems, setPrioritizationItems] = React.useState(value);

  // Make sure a valid array is provided
  if (Object.prototype.toString.call(prioritizationItems) !== '[object Array]') {
    throw new Error('Please provide a valid array');
  }

  const prioritizeItem = (from, to) => {
    const arrayToPrioritize = prioritizationItems;

    // Delete the item from its current position
    const item = arrayToPrioritize.splice(from, 1);

    // Make sure there's an item to move
    if (!item.length) {
      throw new Error(`There is no item in the array at index ${from}`);
    }

    // Move the item to its new position
    arrayToPrioritize.splice(to, 0, item[0]);
    setPrioritizationItems([...arrayToPrioritize]);

    // Callback with new array item prioritization
    onChange([...arrayToPrioritize]);
  };

  // Returns prompt from meta-option list
  const getPrompt = (opt) => {
    const [option] = options.filter(o => o.id === opt);
    return option?.prompt;
  };

  return (
    <Box
      className={classNames(classes.root, className)}
      test={'select-field-prioritization'}
    >
      {value.map((id, index) => {
        let raisePriority = () => prioritizeItem(index, index - 1);
        let lowerPriority = () => prioritizeItem(index, index + 1);

        if (index === 0) raisePriority = null;
        if ((index + 1) === prioritizationItems.length) lowerPriority = null;

        return (
          <ShadedRoundBox
            key={id}
            className={classNames(classes.listItem)}
            test={`select-field-prioritization-item-${getPrompt(id)}`}
          >
            <ListItemText1
              disableTypography
              primary={
                <Typography
                  className={classes.primaryText}
                  variant={'h5'}
                  weight={'bold'}
                  color={'primary'}
                >
                  {getPrompt(id)}
                </Typography>
              }
            />
            <Box className={classes.orderActions}>
              <PrioritySwitcher
                onRaisePriority={raisePriority}
                onLowerPriority={lowerPriority}
              />
            </Box>
          </ShadedRoundBox>
        );
      })}
    </Box>
  );
};

SelectFieldPrioritization.defaultProps = {
  className: '',
};

SelectFieldPrioritization.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default withStyles(styles)(SelectFieldPrioritization);
